"use client";

import { useCallback, useRef, useState } from "react";
import type React from "react";
import type { DragPos, UseDebugDragResult } from "./use-debug-drag.types";

export type { UseDebugDragResult, DragPos };

export function useDebugDrag(): UseDebugDragResult {
  const containerRef = useRef<HTMLDivElement>(null);
  const dragState = useRef({ sx: 0, sy: 0, ex: 0, ey: 0 });
  const isDragging = useRef(false);
  const didDrag = useRef(false);
  const [pos, setPos] = useState<DragPos | null>(null);

  const clampPosition = useCallback((x: number, y: number): DragPos => {
    const rect = containerRef.current?.getBoundingClientRect();
    const width = rect?.width ?? 320;
    const height = rect?.height ?? 200;
    const maxX = Math.max(0, window.innerWidth - width - 12);
    const maxY = Math.max(0, window.innerHeight - height - 12);
    return {
      x: Math.max(12, Math.min(maxX, x)),
      y: Math.max(12, Math.min(maxY, y)),
    };
  }, []);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (
      target !== e.currentTarget &&
      target.closest("button, a, input, select, textarea")
    )
      return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    isDragging.current = true;
    didDrag.current = false;
    dragState.current = {
      sx: e.clientX,
      sy: e.clientY,
      ex: rect.left,
      ey: rect.top,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging.current) return;
      const dx = e.clientX - dragState.current.sx;
      const dy = e.clientY - dragState.current.sy;
      if (!didDrag.current && Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
      didDrag.current = true;
      setPos(
        clampPosition(dragState.current.ex + dx, dragState.current.ey + dy),
      );
    },
    [clampPosition],
  );

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    isDragging.current = false;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // Ignore if capture was already released
    }
  }, []);

  return {
    containerRef,
    pos,
    didDrag,
    dragHandleProps: { onPointerDown, onPointerMove, onPointerUp },
  };
}
